// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — steering controllers used only by the balance/mechanism sims (not shipped
// into the live game's hot path, but real game logic). The SMART bot reads the maze
// and steers the marble along a BFS route (greedy-nearest gems, then the exit),
// avoiding pits — it proves every generated board is bot-completable (principle
// "objective must be bot-completable") and provides the reference "optimal" time. The
// BLIND bot ignores the maze and wanders, which is the control that shows the maze
// navigation is load-bearing (a negative gap proves the mechanic).

import { cellCenter, pathBetween, type Cell, type Maze } from './maze';
import type { GameState } from './game';
import { clampVector } from './tilt';

export interface Controller {
  tilt(s: GameState): { x: number; y: number };
}

function holeBlockedSet(maze: Maze): Set<number> {
  return new Set(maze.holes.map((h) => h.gy * maze.gw + h.gx));
}

function ballCell(s: GameState): Cell {
  const gx = Math.min(s.maze.gw - 1, Math.max(0, Math.floor(s.ball.x)));
  const gy = Math.min(s.maze.gh - 1, Math.max(0, Math.floor(s.ball.y)));
  return { gx, gy };
}

/** The current goal cell: the nearest uncollected gem, else the exit. */
function goalCell(s: GameState, blocked: Set<number>): Cell {
  const from = ballCell(s);
  if (s.gemsLeft > 0) {
    let best: Cell | null = null;
    let bestLen = Infinity;
    for (let i = 0; i < s.maze.gems.length; i++) {
      if (s.collected[i]) continue;
      const p = pathBetween(s.maze, from, s.maze.gems[i], blocked);
      if (p && p.length < bestLen) {
        bestLen = p.length;
        best = s.maze.gems[i];
      }
    }
    if (best) return best;
  }
  return s.maze.exit;
}

export class SmartBot implements Controller {
  private readonly blocked: Set<number>;
  constructor(maze: Maze) {
    this.blocked = holeBlockedSet(maze);
  }
  tilt(s: GameState): { x: number; y: number } {
    const from = ballCell(s);
    const goal = goalCell(s, this.blocked);
    const path = pathBetween(s.maze, from, goal, this.blocked);
    let tx: number;
    let ty: number;
    if (!path || path.length < 2) {
      const c = cellCenter(goal);
      tx = c.x;
      ty = c.y;
    } else {
      // Aim at the first node clearly ahead of us, so we flow through corners.
      let node = path[path.length - 1];
      for (const c of path) {
        const cc = cellCenter(c);
        if (Math.hypot(cc.x - s.ball.x, cc.y - s.ball.y) > 0.55) {
          node = c;
          break;
        }
      }
      const c = cellCenter(node);
      tx = c.x;
      ty = c.y;
    }
    let dx = tx - s.ball.x;
    let dy = ty - s.ball.y;
    const d = Math.hypot(dx, dy) || 1;
    dx /= d;
    dy /= d;
    // Brake against current velocity so it does not overshoot turns.
    return clampVector(dx - s.ball.vx * 0.11, dy - s.ball.vy * 0.11);
  }
}

/** A wanderer with no maze knowledge — the load-bearing control. Deterministic. */
export class BlindBot implements Controller {
  private hx = 0;
  private hy = 1;
  private ttl = 0;
  private rng: () => number;
  constructor(seed: number) {
    let a = (seed ^ 0x9e3779b9) >>> 0;
    this.rng = () => {
      a += 0x6d2b79f5;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  tilt(_s: GameState): { x: number; y: number } {
    if (this.ttl <= 0) {
      const a = this.rng() * Math.PI * 2;
      this.hx = Math.cos(a);
      this.hy = Math.sin(a);
      this.ttl = 0.25 + this.rng() * 0.6; // hold a heading for a fraction of a second
    }
    this.ttl -= 1 / 60;
    return { x: this.hx, y: this.hy };
  }
}
